import { between, minValue, numeric, requiredUnless } from 'vuelidate/lib/validators';
import { getQuotaDependentFieldLabel, getSelectedItemsTitle } from './common';
import {
  csmAccountCountTitles,
  csmBusinessValueTitles,
  csmQuotaTypeTitles,
  csmRenewalValueTitles,
  jobsCsmQuotaPeriodNames,
} from '@/components/partials/jobs/constants/optionsDicts';
import { csmQuotaMetrics } from '@/components/partials/jobs/constants/selectableOptions';
import {
  QUOTA_ATTAINMENT_BASE_LABEL,
  QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL,
} from '@/components/partials/jobs/JobsProfile/constants';
import type {
  Form,
  FormValidations,
  GetValidationPayload,
  InquiryForm,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import type { JobsInquiry } from '@/types/graphql';
import { JobsInquiryCsmQuota, JobsInquiryCsmQuotaMetric, JobsInquiryCsmQuotaPeriod } from '@/types/graphql';

export const getQuotaStructureFieldValue = (inquiry: InquiryForm) => {
  if (inquiry?.csmQuotaTypeMain) return csmQuotaTypeTitles[inquiry.csmQuotaTypeMain];

  return inquiry?.csmQuotaTypeOther || undefined;
};

export const getQuotaSystemValue = (inquiry: InquiryForm) => {
  const { csmQuotaMetricMain, csmQuotaMetricOther } = inquiry;

  const mainMetricTitles = getSelectedItemsTitle(csmQuotaMetricMain || [], csmQuotaMetrics);

  return [...mainMetricTitles, csmQuotaMetricOther].filter(Boolean).join(', ');
};

export const getQuotaPeriodFieldValue = (inquiry: InquiryForm) => {
  if (inquiry?.csmQuotaPeriodMain) return jobsCsmQuotaPeriodNames[inquiry.csmQuotaPeriodMain];

  return inquiry?.csmQuotaPeriodOther || undefined;
};

export const getLastPeriodQuotaAttainment = (inquiry: InquiryForm) => {
  if (!inquiry?.quotaAttainmentPerPeriod) return undefined;

  return `${inquiry.quotaAttainmentPerPeriod}%`;
};

export const getLastYearQuotaAttainment = (inquiry: InquiryForm) => {
  if (!inquiry?.quotaAttainmentPerLastYear) return undefined;

  return `${inquiry.quotaAttainmentPerLastYear}%`;
};

export const getAverageRenewalsFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.csmRenewalValue) return undefined;

  return csmRenewalValueTitles[inquiry.csmRenewalValue];
};

export const getAverageUpsellsFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.csmUpsellValue) return undefined;

  return csmRenewalValueTitles[inquiry.csmUpsellValue];
};

export const getAccountNumberFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.csmAccountsNumber) return undefined;

  return csmAccountCountTitles[inquiry.csmAccountsNumber];
};

export const getBookOfBusinessFieldValue = (inquiry: InquiryForm) => {
  if (!inquiry?.csmTotalBusinessValue) return undefined;

  return csmBusinessValueTitles[inquiry.csmTotalBusinessValue];
};

export const validateEditSalarySectionModalForm = ({
  hasAdminPrivilege,
  form,
}: GetValidationPayload): FormValidations => {
  return {
    baseSalary: {
      numeric,
      greaterThanZero: hasAdminPrivilege ? () => true : minValue(1),
    },
    ote: {
      required: requiredUnless(() => hasAdminPrivilege),
      numeric,
    },
    revenue: {
      numeric,
      greaterThanZero: hasAdminPrivilege ? () => true : minValue(1),
    },
    csmTotalBusinessValue: {
      required: requiredUnless(() => hasAdminPrivilege),
    },
    csmAccountsNumber: {
      required: requiredUnless(() => hasAdminPrivilege),
    },
    rankOnTeam: {
      required: requiredUnless(() => hasAdminPrivilege),
      numeric,
      greaterThanZero: hasAdminPrivilege ? () => true : minValue(1),
    },
    teamSize: {
      required: requiredUnless(() => hasAdminPrivilege),
      numeric,
      greaterThanZero: hasAdminPrivilege ? () => true : minValue(1),
    },
    quotaAttainmentByTeamPerPeriod: {
      required: (value: number) => {
        if (hasAdminPrivilege) return true;

        return form.csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota || Boolean(value);
      },
      numeric,
      between: between(0, 100),
    },
  };
};

export const getIsQoutaFliedsHidden = (inquiry: InquiryForm) => {
  return inquiry?.csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota;
};

export const getIsQoutaAttainmentLastYearFieldIsHidden = ({ csmQuotaPeriodMain }: Form) => {
  return csmQuotaPeriodMain === JobsInquiryCsmQuotaPeriod.Annual;
};

export const getIsEditModalQuotaFieldIsHidden = ({ csmQuotaTypeMain }: Form): boolean => {
  return getIsQoutaFliedsHidden({ csmQuotaTypeMain } as JobsInquiry);
};

export const hasRenewalsOrUpselsInQuotaMeasuring = ({
  csmQuotaTypeMain,
  csmQuotaMetricMain,
}: {
  csmQuotaTypeMain?: JobsInquiryCsmQuota | null;
  csmQuotaMetricMain: string[] | undefined | null;
}) => {
  if (csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota) return false;
  if (!Array.isArray(csmQuotaMetricMain)) return false;

  return csmQuotaMetricMain.some(metric =>
    [JobsInquiryCsmQuotaMetric.CsmRenewals, JobsInquiryCsmQuotaMetric.CsmUpsells].includes(
      metric as JobsInquiryCsmQuotaMetric,
    ),
  );
};

export const hasUpselsInQuotaMeasuring = ({
  csmQuotaTypeMain,
  csmQuotaMetricMain,
}: {
  csmQuotaTypeMain?: JobsInquiryCsmQuota | null;
  csmQuotaMetricMain: string[] | undefined | null;
}) => {
  if (csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota) return false;
  if (!Array.isArray(csmQuotaMetricMain)) return false;

  return csmQuotaMetricMain.includes(JobsInquiryCsmQuotaMetric.CsmUpsells);
};

export const hasRenewalsInQuotaMeasuring = (csmQuotaMetricMain: string[] | undefined | null) => {
  if (!Array.isArray(csmQuotaMetricMain)) return false;

  return csmQuotaMetricMain.includes(JobsInquiryCsmQuotaMetric.CsmRenewals);
};

export const getCsmQuotaAttainmentEditFieldLabel = (form: Form) => {
  if (form?.csmQuotaPeriodOther) return QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL;
  if (!form?.csmQuotaPeriodMain) return QUOTA_ATTAINMENT_LAST_PERIOD__SHORT_LABEL;

  return getQuotaDependentFieldLabel({
    periodKey: form.csmQuotaPeriodMain,
    baseLabel: QUOTA_ATTAINMENT_BASE_LABEL,
    periodPrefix: 'last',
    postfix: '?',
  });
};
